import React, { PropsWithChildren } from "react";

import { Portal } from "shared/common-ui/Portal";

import PopoverContent from "./PopoverContent/PopoverContent";
import PopoverEventer from "./PopoverEventer/PopoverEventer";
import PopoverTrigger from "./PopoverTrigger/PopoverTrigger";
import {
  PopoverContentProps,
  PopoverProps,
  PopoverTriggerProps,
} from "./types";

const Popover: React.FC<PropsWithChildren<PopoverProps>> & {
  Trigger: React.FC<
    Pick<
      React.ComponentProps<typeof PopoverTrigger>,
      "className" | "tagName" | "children"
    >
  >;
  Content: React.FC<
    Pick<React.ComponentProps<typeof PopoverContent>, "className" | "children">
  >;
} = ({
  children,
  event = "click",
  placement,
  offset,
  isEqualWidth = false,
  appendTo = "viewport",
  onClose,
  isOpenPopover = false,
}) => {
  let TriggerComponent!: React.ReactElement;
  let ContentComponent!: React.ReactElement;

  React.Children.toArray(children).forEach((child) => {
    if (React.isValidElement(child)) {
      if (child.type === PopoverTrigger) {
        TriggerComponent = child;
      }

      if (child.type === PopoverContent) {
        ContentComponent = child;
      }
    }
  });

  if (!TriggerComponent || !ContentComponent) {
    throw new Error(
      "Children Popover.Trigger or Popover.Content don`t present"
    );
  }

  return (
    <PopoverEventer
      event={event}
      onClose={onClose}
      isOpenPopover={isOpenPopover}
    >
      {({ isOpen, close, triggerRef, popoverRef }): React.ReactElement => (
        <>
          {React.cloneElement(TriggerComponent, {
            isOpen,
            triggerRef,
          })}
          {isOpen && (
            <Portal
              isFixedBody={false}
              appendTo={
                appendTo === "parent" && triggerRef.current
                  ? triggerRef.current
                  : undefined
              }
            >
              {React.cloneElement(ContentComponent, {
                triggerRef,
                popoverRef,
                placement,
                offset,
                isEqualWidth,
                close,
                appendTo,
              })}
            </Portal>
          )}
        </>
      )}
    </PopoverEventer>
  );
};

Popover.Trigger = PopoverTrigger as React.FC<
  Omit<
    PopoverTriggerProps,
    Exclude<keyof PopoverTriggerProps, "className" | "tagName" | "children">
  >
>;
Popover.Content = PopoverContent as React.FC<
  Omit<
    PopoverContentProps,
    Exclude<keyof PopoverContentProps, "className" | "children">
  >
>;

export default Popover;
